import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import './PacingUpload.css';
import { Box, Typography, Container, CircularProgress, Button } from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import DatasetIcon from '@mui/icons-material/Dataset';
import axios from 'axios';
import PacingUploadExcel from '../../components/PacingUploadExcel/PacingUploadExcel';
import PageTitle from '../../components/PageTitle/PageTitle';

const base_url = 'https://jasperd.ca';

const PacingUpload = () => {
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const pacingExcelRef = useRef(null);

  /**
   * 1) Moved all "send to backend" code to a new handler that expects the raw file:
   */
  const handleExcelFileSelected = async (excelFile) => {
    if (!excelFile) return;
    setLoading(true);

    try {
      // Prepare FormData to send the raw excel file
      const formData = new FormData();
      formData.append('file', excelFile);

      // Post the raw file to your backend
      const response = await axios.post(`${base_url}/api/submit-pacing-excel`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        validateStatus: () => true,
      });

      // Once you have your task ID, start polling
      const { task_id } = response.data;
      pollTaskStatus(task_id);
    } catch (error) {
      console.error('Error submitting Excel file to backend:', error);
      setLoading(false);
    }
  };

  /**
   * 2) Polling logic remains the same, except we no longer need the parsedData:
   */
  const pollTaskStatus = (taskId) => {
    const interval = setInterval(() => {
      axios.get(`${base_url}/api/task-status/${taskId}`, {
        validateStatus: () => true,
      })
      .then(async (response) => {
        const result = response.data;
        console.log(result)
        if (result.status === 'completed') {
          clearInterval(interval);
          try {
            const pacingData = result?.result || {};
            setLoading(false);

            // If the backend returns final processed data:
            const basePath = window.location.pathname.startsWith('/insights') ? '/insights' : '';
            const targetPath = `${basePath}/tpm/tools/pacing/analysis`;
            navigate(targetPath, { state: pacingData });
          } catch (error) {
            console.error('Error processing backend data:', error);
            setLoading(false);
          }
        }
        else if (result.status === 'error') {
          console.error('Task failed:', response.data.error);
          setLoading(false);
          clearInterval(interval);
        }
      })
      .catch((error) => {
        console.error('Error fetching task status:', error);
        setLoading(false);
        clearInterval(interval);
      });
    }, 3000);
  };

  const handleDownloadTemplate = () => {
    const link = document.createElement('a');
    link.href = `${process.env.PUBLIC_URL}/Private Asset Pacing Inputs.xlsx`;
    link.download = 'Private Asset Pacing Inputs.xlsx';
    link.click();
  };

  // Optional helper for using an example file
  const handleUseExampleData = () => {
    axios.get(`${process.env.PUBLIC_URL}/Private Asset Pacing Inputs.xlsx`, {
      responseType: 'blob',
    })
    .then((response) => {
      const file = new File([response.data], 'Private Asset Pacing Inputs.xlsx', { type: response.data.type });
      // Forward it to the child component
      pacingExcelRef.current.processExternalFile(file);
    })
    .catch((error) => {
      console.error('Error using example data:', error);
    });
  };

  return (
    <div className="pacing-upload-screen">
      <Container className="pacing-upload-container">
        <Box sx={{ textAlign: 'center', my: 4 }}>
          <PageTitle 
            title="Private Investment Pacing Model"
            subtitle="Upload" 
          />
        </Box>

        {/* 3) Pass handleExcelFileSelected to PacingUploadExcel */}
        <Box sx={{ my: 3 }}>
          <PacingUploadExcel
            ref={pacingExcelRef}
            onFileSelected={handleExcelFileSelected}
          />
        </Box>

        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
            <CircularProgress />
          </Box>
        )}

        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, my: 3, flexWrap: 'wrap' }}>
          <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            onClick={handleDownloadTemplate}
            sx={{ minWidth: '200px' }}
          >
            Download Excel Template
          </Button>
          <Button
            variant="contained"
            startIcon={<DatasetIcon />}
            onClick={handleUseExampleData}
            sx={{ minWidth: '200px' }}
          >
            Use Example Data
          </Button>
        </Box>
      </Container>
    </div>
  );
};

export default PacingUpload;
